import React, { useCallback, useState } from 'react';
import LogRocket from 'logrocket';
import { Platform, Pressable } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Inline,
  IBoxProps,
} from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import analytics from '../../../../modules/analytics';
import nonCriticalException from '../../../../modules/exceptionLogger';
import {
  CALENDAR_ROUTE,
  HOME_ROUTE,
  PROFILE_EDIT_ROUTE,
} from '../../../../route-names';
import ProAvatarIcon from '../../../ProAvatarIcon';
import {
  BodyText,
  HeaderText,
  Stack,
} from '../../../ui';
import { DesktopPopover } from '../../../ui/Popover';
import type { IRootDispatch, RootState } from '../../../../store/models';
import { useParams, useLinkTo } from '../../../../hooks';
import StateLink from '../../../StateLink';
import { addQueryStringParams } from '../../../../modules/url';
import { SUPPORT_PAGE_URL } from '../../../../modules/SupportServiceWrapper';
import { clickable } from '../../../../styles';
import { ChevronIcon } from '../../../ui/Icons';
import { ActionLink } from '../../../admin/Layout/ActionLink';

export const Hoverable = (props: IBoxProps) => {
  const [hover, setHover] = useState<boolean>(false);

  return (
    <Box
      {...props}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      color={hover ? BrandColor.SuperLight : props.color}
    />
  );
};

const ProHeaderDropdown = ({
  providerId,
  isLandingRedesignEnabled = false,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [shouldStayOpen, setShouldStayOpen] = useState<boolean>(false);

  const linkTo = useLinkTo();
  const dispatch = useDispatch<IRootDispatch>();
  const params = useParams() || {};
  const proName = useSelector<RootState, string>(state => (
    // eslint-disable-next-line eqeqeq
    state.user?.providers?.find(pro => pro.id == providerId)?.name
  ));
  const subProviders = useSelector<RootState, { id: number; name: string }[]>(state => (
    state.user?.providers
  )) || [];
  const {
    utm_source,
    utm_campaign,
    utm_content,
    utm_medium,
    utm_term,
  } = params;

  const proParams = {
    providerId,
  };
  const homeParams = {
    mode: 'week',
    ...params,
    providerId,
  };
  const utmParams = {
    utm_source,
    utm_campaign,
    utm_content,
    utm_medium,
    utm_term,
  };

  const handleToggleClick = useCallback(() => {
    if (open && !shouldStayOpen) {
      setShouldStayOpen(true);
    } else {
      setOpen(!open);
      setShouldStayOpen(!shouldStayOpen);
    }
  }, [
    open,
    shouldStayOpen,
  ]);

  const handleHoverOut = useCallback(() => {
    if (shouldStayOpen) {
      return;
    }

    setOpen(false);
  }, [shouldStayOpen]);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleLogout = async () => {
    try {
      const eventName = 'header_dropdown_home_logout_click';
      const data = {
        origin: 'header_nav', platform: Platform.OS, isProvider: true, isClient: false,
      };
      LogRocket.track(eventName, data);
      analytics.track(eventName, data);
    } catch (e) {
      nonCriticalException(e, { from: 'ProHeaderDropdown', action: 'logout' });
    }
    try {
      analytics.track('header_dropdown_home_logout_click', {
        origin: 'header_nav', platform: Platform.OS, isProvider: true, isClient: false,
      });
      await dispatch.user.logout();

      handleClose();
      if (Platform.OS === 'web') {
        window.location.assign('/');
      } else {
        linkTo(HOME_ROUTE, undefined, undefined, true);
      }
    } catch (e) {
      nonCriticalException(e, { from: 'ProHeaderDropdown', action: 'logout' });
    }
  };

  const handleSwitchAccount = (id: number) => {
    const eventName = 'header_dropdown_home_switch_acc_click';
    const data = {
      origin: 'header_nav', platform: Platform.OS, isProvider: true, isClient: false,
    };
    try {
      LogRocket.track(eventName, data);

      analytics.track('header_dropdown_home_switch_acc_click', {
        origin: 'header_nav', platform: Platform.OS, isProvider: true, isClient: false,
      });
    } catch (e) {
      nonCriticalException(e, { from: 'ProHeaderDropdown', action: 'switch_account' });
    }

    handleClose();
    dispatch.route.addParams({ params: { providerId: id } });
  };

  if (!providerId) {
    return null;
  }

  const analyticsParams = {
    platform: Platform.OS, isProvider: true, isClient: false,
  };

  const menuStyle = isLandingRedesignEnabled ? {
    borderBottom: `1px solid ${BrandColor.LightGrey}`,
    paddingBottom: 16,
    borderBottomColor: BrandColor.LightGrey,
  } : {};

  const textAppearance = isLandingRedesignEnabled ? 'normal' : 'small';

  return (
    <Box
      position="relative"
    >
      <Pressable
        onPress={handleToggleClick}
        onHoverIn={handleOpen}
        testID="proAccountMenuToggle"
      >
        <Inline
          alignItems="center"
          space={8}
          // @ts-ignore
          style={{
            userSelect: 'none',
            marginTop: 16,
            marginBottom: 16,
            ...clickable,
          }}
        >
          {isLandingRedesignEnabled ? (
            <React.Fragment>
              <HeaderText
                level={3}
                color={BrandColor.BackInBlack}
                style={{
                  paddingLeft: 4,
                }}
              >
                My Settings
              </HeaderText>

              <ChevronIcon direction={open ? 'up' : 'down'} />
            </React.Fragment>
          ) : ''}
          <ProAvatarIcon
            providerId={providerId}
            sizeClass="extra-small"
          />
          {subProviders.length <= 1 ? null : (
            <BodyText color={BrandColor.Metal}>{proName}</BodyText>
          )}
        </Inline>
      </Pressable>
      {open ? (
        <DesktopPopover
          name="proHeaderDropdown"
          borderRadius={isLandingRedesignEnabled ? 8 : 0}
          borderColor={isLandingRedesignEnabled ? BrandColor.LightGrey : BrandColor.Silver}
          top={50}
          right={-5}
          onClose={() => setOpen(false)}
        >
          <Pressable onHoverOut={handleHoverOut}>
            <Stack
              paddingY={12}
              minWidth={isLandingRedesignEnabled ? 280 : 172}
              style={{ marginBottom: 4 }}
            >
              <StateLink
                testID="header-dropdown-home-link"
                to={CALENDAR_ROUTE}
                params={homeParams}
                onClick={handleClose}
                clickTracking={{
                  name: 'header_dropdown_home_link_click',
                  params: { origin: 'header_nav', ...analyticsParams },
                }}
              >
                <Hoverable
                  paddingX={24}
                  paddingY={10}
                >
                  <BodyText
                    appearance={textAppearance}
                    color={BrandColor.Metal}
                    style={{ ...menuStyle }}
                  >
                    Home
                  </BodyText>
                </Hoverable>
              </StateLink>
              <StateLink
                testID="header-dropdown-directory-link"
                to={HOME_ROUTE}
                onClick={handleClose}
                clickTracking={{
                  name: 'header_dropdown_directory_link_click',
                  params: { origin: 'header_nav', ...analyticsParams },
                }}
              >
                <Hoverable
                  paddingX={24}
                  paddingY={10}
                >
                  <BodyText
                    appearance={textAppearance}
                    color={BrandColor.Metal}
                    style={{ ...menuStyle }}
                  >
                    Directory
                  </BodyText>
                </Hoverable>
              </StateLink>
              <StateLink
                testID="header-dropdown-profile-link"
                to={PROFILE_EDIT_ROUTE}
                params={proParams}
                onClick={handleClose}
                clickTracking={{
                  name: 'header_dropdown_profile_link_click',
                  params: { origin: 'header_nav', ...analyticsParams },
                }}
              >
                <Hoverable
                  paddingX={24}
                  paddingY={10}
                >
                  <BodyText
                    appearance={textAppearance}
                    color={BrandColor.Metal}
                    style={{ ...menuStyle }}
                  >
                    Profile
                  </BodyText>
                </Hoverable>
              </StateLink>
              <StateLink
                testID="header-dropdown-account-link"
                to="provider.settings_menu.account"
                params={proParams}
                onClick={handleClose}
                clickTracking={{
                  name: 'header_dropdown_account_link_click',
                  params: { origin: 'header_nav', ...analyticsParams },
                }}
              >
                <Hoverable
                  paddingX={24}
                  paddingY={10}
                >
                  <BodyText
                    appearance={textAppearance}
                    color={BrandColor.Metal}
                    style={{ ...menuStyle }}
                  >
                    Account
                  </BodyText>
                </Hoverable>
              </StateLink>
              <StateLink
                testID="header-dropdown-settings-link"
                to="provider.settings_menu.more"
                params={proParams}
                onClick={handleClose}
                clickTracking={{
                  name: 'header_dropdown_settings_link_click',
                  params: { origin: 'header_nav', ...analyticsParams },
                }}
              >
                <Hoverable
                  paddingX={24}
                  paddingY={10}
                >
                  <BodyText
                    appearance={textAppearance}
                    color={BrandColor.Metal}
                    style={{ ...menuStyle }}
                  >
                    Settings
                  </BodyText>
                </Hoverable>
              </StateLink>
              <StateLink
                testID="header-dropdown-help-center-link"
                href={addQueryStringParams(SUPPORT_PAGE_URL, utmParams)}
                onClick={handleClose}
                clickTracking={{
                  name: 'header_dropdown_help_center_link_click',
                  params: { origin: 'header_nav', ...analyticsParams },
                }}
              >
                <Hoverable
                  paddingX={24}
                  paddingY={10}
                >
                  <BodyText
                    appearance={textAppearance}
                    color={BrandColor.Metal}
                    style={{ ...menuStyle }}
                  >
                    Help Center
                  </BodyText>
                </Hoverable>
              </StateLink>
              <ActionLink
                testID="header-dropdown-logout-link"
                onClick={handleLogout}
              >
                <Hoverable
                  paddingX={24}
                  paddingY={10}
                  width="100%"
                  style={clickable}
                >
                  <BodyText
                    appearance={textAppearance}
                    color={BrandColor.Metal}
                  >
                    Logout
                  </BodyText>
                </Hoverable>
              </ActionLink>
              {subProviders.length <= 1 ? null : (
                <Stack>
                  <Box paddingX={24}>
                    <Box
                      paddingY={10}
                      borderTopWidth={1}
                      borderColor={BrandColor.Silver}
                      borderStyle="solid"
                      testID="header-dropdown-switch-accounts-link"
                    >
                      <BodyText
                        color={BrandColor.Metal}
                        appearance={isLandingRedesignEnabled ? 'small' : 'smaller'}
                        whiteSpace="nowrap"
                      >
                        SWITCH ACCOUNTS
                      </BodyText>
                    </Box>
                  </Box>
                  {subProviders.map(acct => (
                    <Pressable
                      key={acct.id}
                      onPress={() => handleSwitchAccount(acct.id)}
                      testID={`switchToPro-${acct.id}`}
                    >
                      <Hoverable
                        paddingX={24}
                        paddingY={10}
                        style={clickable}
                      >
                        <BodyText
                          color={BrandColor.Metal}
                          appearance={isLandingRedesignEnabled ? 'small' : 'smaller'}
                          whiteSpace="nowrap"
                        >
                          {acct.name}
                        </BodyText>
                      </Hoverable>
                    </Pressable>
                  ))}
                </Stack>
              )}
            </Stack>
          </Pressable>
        </DesktopPopover>
      ) : null}
    </Box>
  );
};

export default ProHeaderDropdown;
